/**
 * MarketplacePublic — anonymous-friendly marketplace marketing page.
 * Routes: /marketplace
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import {
  Briefcase, Search, Sparkles, Users, GraduationCap, ArrowRight,
  Globe, Award, Shield, MapPin, Eye, BookOpen, Building2,
} from "lucide-react";

export default function MarketplacePublic() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Best-effort marketplace facets — gives us category count chips
    api.get("/marketplace/facets").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <Layout title="TeeBee Educator Marketplace" anonymous={!user && !authLoading}>
      {/* Hero */}
      <section className="tb-card-feature relative overflow-hidden" data-testid="marketplace-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br from-primary/30 to-amber-200/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl">
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Briefcase className="h-3 w-3" /> TeeBee Educator Marketplace
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-tight">
            Discover curricular and extra-curricular educators globally.
          </h1>
          <p className="mt-3 text-base text-muted-foreground max-w-2xl">
            One AI-native platform where teachers showcase their craft and institutions find their next great educator. Privacy-first. Premium. Built for the global classroom.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={() => navigate(user ? "/profile" : "/signup?role=teacher")} className="rounded-full h-11 px-5" data-testid="mp-cta-teacher">
              <GraduationCap className="h-4 w-4 mr-2" /> Join as Teacher
            </Button>
            <Button onClick={() => navigate(user ? "/hiring" : "/signup?role=hiring")} variant="outline" className="rounded-full h-11 px-5" data-testid="mp-cta-hire">
              <Building2 className="h-4 w-4 mr-2" /> Hire Educators
            </Button>
            <Button onClick={() => navigate("/explore")} variant="ghost" className="rounded-full h-11 px-5" data-testid="mp-cta-explore">
              <BookOpen className="h-4 w-4 mr-2" /> Explore lessons
            </Button>
          </div>
        </div>
      </section>

      {/* Side-by-side value props */}
      <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5 tb-stagger">
        <SidePanel
          icon={GraduationCap} chip="tb-chip-lavender"
          eyebrow="For Educators"
          title="Get discovered. Get hired. Grow your craft."
          bullets={[
            "Build a premium AI Educator Profile + CV in minutes",
            "Get discovered by schools across India and globally",
            "Apply to roles with AI-generated cover letters",
            "Showcase public lessons + portfolio links",
            "Earn TeeBee Coins through referrals and content",
          ]}
          ctaLabel="Create TeeBee Profile"
          onCta={() => navigate(user ? "/profile" : "/signup?role=teacher")}
          testid="mp-panel-teacher"
        />
        <SidePanel
          icon={Building2} chip="tb-chip-sky"
          eyebrow="For Institutions & Recruiters"
          title="Find verified educators. Across every category."
          bullets={[
            "Search 24 curricular + extra-curricular categories",
            "Permission-safe CV requests — teachers approve access",
            "Post jobs and reach educators in your region",
            "Match scores grade candidates against your role",
            "Institution hiring dashboard with team-level controls",
          ]}
          ctaLabel="Open Hiring Hub"
          onCta={() => navigate(user ? "/hiring" : "/signup?role=hiring")}
          testid="mp-panel-hiring"
        />
      </section>

      {/* Category strip */}
      {stats && (
        <section className="mt-6" data-testid="mp-categories">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
            <Sparkles className="h-3 w-3" /> 24 categories — curricular & extra-curricular
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[...(stats.curricular_categories || []), ...(stats.extra_curricular_categories || [])].map((c) => (
              <span key={c} className="rounded-full text-[11px] px-2.5 py-1 border border-border bg-card/60">{c}</span>
            ))}
          </div>
        </section>
      )}

      {/* Trust strip */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 tb-stagger">
        <Trust icon={Shield}  title="Privacy-first by design"    body="Teachers control every field recruiters see — phone, email, CV access, salary band — nothing leaks." />
        <Trust icon={Award}   title="Verified educator badges"   body="TeeBee Verified, Top Creator, Institution Verified and more — surfaced on profile + discovery cards." />
        <Trust icon={Globe}   title="Curricular + extra-curricular" body="Maths to Music · Robotics to Yoga · Preschool to University — the entire spectrum of educators." />
      </section>

      {/* Footer CTA */}
      <section className="mt-10 tb-card-feature relative overflow-hidden">
        <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
        <div className="relative text-center max-w-2xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Free during beta</p>
          <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Join the educator network that grows with you.</h2>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <Button onClick={() => navigate(user ? "/profile" : "/signup")} className="rounded-full h-11 px-5">
              {user ? "Open my profile" : "Create free account"} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function SidePanel({ icon: Icon, chip, eyebrow, title, bullets, ctaLabel, onCta, testid }) {
  return (
    <div className="tb-card p-6" data-testid={testid}>
      <span className={`h-10 w-10 rounded-2xl ${chip} grid place-items-center`}>
        <Icon className="h-5 w-5" />
      </span>
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mt-3">{eyebrow}</p>
      <h2 className="mt-1 font-display text-2xl tracking-tight font-medium">{title}</h2>
      <ul className="mt-4 space-y-2 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2"><ArrowRight className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" /> {b}</li>
        ))}
      </ul>
      <Button className="rounded-full mt-5" onClick={onCta}>{ctaLabel} <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Button>
    </div>
  );
}

function Trust({ icon: Icon, title, body }) {
  return (
    <div className="tb-card p-5">
      <span className="h-9 w-9 rounded-xl tb-chip-mint grid place-items-center">
        <Icon className="h-4 w-4" />
      </span>
      <p className="font-display text-base font-medium mt-3">{title}</p>
      <p className="text-[12px] text-muted-foreground mt-1">{body}</p>
    </div>
  );
}
